import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Star, ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";

const prettyDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`star-${n}`}
          className="transition-transform duration-150 hover:scale-110">
          <Star size={26} className={n <= value ? "text-primary" : "text-muted-foreground"} fill={n <= value ? "#C5A059" : "none"} />
        </button>
      ))}
    </div>
  );
}

export default function RateVisit() {
  const [mobile, setMobile] = useState("");
  const [visits, setVisits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({});

  const lookup = async () => {
    if (mobile.trim().length < 5) { toast.error("Enter the mobile number used for your booking"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/appointments/public/reviewable", { params: { mobile: mobile.trim() } });
      setVisits(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submit = async (v) => {
    const d = draft[v.appointment_id] || {};
    if (!d.rating) { toast.error("Please pick a star rating"); return; }
    try {
      await api.post("/appointments/public/review", {
        appointment_id: v.appointment_id, mobile: mobile.trim(), rating: d.rating, review: d.review || "",
      });
      toast.success(`Thanks for rating ${v.staff_name}!`);
      setVisits((prev) => prev.map((x) => x.appointment_id === v.appointment_id ? { ...x, already_rated: true } : x));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pending = (visits || []).filter((v) => !v.already_rated);
  const rated = (visits || []).filter((v) => v.already_rated);

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border flex items-center px-6 backdrop-blur-xl bg-background/80 sticky top-0 z-10">
        <Logo className="text-xl" />
        <Link to="/book" className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="rate-back-book"><ChevronLeft size={14} /> Booking</Link>
      </header>

      <div className="max-w-2xl mx-auto p-6">
        <h1 className="font-display text-3xl mb-1">Rate your visit</h1>
        <p className="text-sm text-muted-foreground mb-6">Enter your mobile number to rate the stylists from your completed appointments.</p>

        <div className="flex gap-2 mb-8 max-w-md">
          <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile number"
            onKeyDown={(e) => e.key === "Enter" && lookup()} className="bg-secondary" data-testid="rate-mobile-input" />
          <Button onClick={lookup} disabled={loading} data-testid="rate-lookup-btn"
            className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{loading ? "Checking…" : "Find visits"}</Button>
        </div>

        {visits !== null && visits.length === 0 && (
          <Card className="p-8 bg-card border-border text-center text-muted-foreground" data-testid="rate-none">
            No completed visits found for this number yet. Ratings unlock after your appointment is completed.
          </Card>
        )}

        {pending.map((v) => {
          const d = draft[v.appointment_id] || {};
          return (
            <Card key={v.appointment_id} className="p-5 bg-card border-border mb-4" data-testid={`rate-visit-${v.appointment_id}`}>
              <p className="font-medium">{v.staff_name}</p>
              <p className="text-xs text-muted-foreground mb-3">{v.service_name} · {prettyDate(v.date)} · {v.branch_name}</p>
              <Stars value={d.rating || 0} onChange={(r) => setDraft((p) => ({ ...p, [v.appointment_id]: { ...d, rating: r } }))} />
              <Input value={d.review || ""} onChange={(e) => setDraft((p) => ({ ...p, [v.appointment_id]: { ...d, review: e.target.value } }))}
                placeholder="Add a short review (optional)" className="bg-secondary mt-3" data-testid={`rate-review-${v.appointment_id}`} />
              <Button onClick={() => submit(v)} data-testid={`rate-submit-${v.appointment_id}`}
                className="mt-3 rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Submit rating</Button>
            </Card>
          );
        })}

        {rated.length > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Already rated</p>
            {rated.map((v) => (
              <div key={v.appointment_id} className="flex items-center gap-2 text-sm text-muted-foreground py-1.5" data-testid={`rated-${v.appointment_id}`}>
                <Check size={14} className="text-primary" /> {v.staff_name} · {v.service_name} · {prettyDate(v.date)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
